import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Box, Typography, useTheme, IconButton } from '@mui/material';

import Item from '../Components/Item';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import OilBarrelOutlinedIcon from '@mui/icons-material/OilBarrelOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { tokens } from '../theme';

import LogOutButton from '../Components/LogoutButton/LogOutButton';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';

import { supabase } from '../Utilities/SupabaseClient';

import OneLineInput from '../Components/OneLineInput/OneLineInput';
import Button from '@mui/material/Button/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import avatarImg from  '../images/avatar.png';

import 'react-pro-sidebar/dist/css/styles.css';

// This component renders the sidebar at the left side of the Dashboard.
const SidebarPanel = () => {
  const theme = useTheme();
  const auth = useAuth();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const initialState = checkInitialState(location);
  const [selected, setSelected] = useState(initialState);

  const handleSignOut = async () => {
    await auth.signOut();
  };

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    getProfile();
  };

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const getProfile = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    try {
      let { data } = await supabase
        .from('user_details')
        .select(`first_name, last_name, role, profile_image, uuid`)
        .eq('uuid', user.id);
      if (!data.length) {
        await supabase
          .from('user_details')
          .insert({ uuid: user.id });
      } else {
        setFirstName(data[0].first_name);
        setLastName(data[0].last_name);
        setRole(data[0].role);
        setImageUrl(data[0].profile_image);
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const updates = {
        first_name: firstName,
        last_name: lastName,
        role: role,
        profile_image: imageUrl,
      };

      let { error } = await supabase
        .from('user_details')
        .update(updates)
        .eq('uuid', user.id);

      if (error) {
        throw error;
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setOpen(false);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  let imageToDisplay = avatarImg;
  if (imageUrl) imageToDisplay = imageUrl; 
  return (
    <>
      <nav>
        <Box
          // Styles the React Pro Sidebar
          sx={{
            height: '100%',
            background: `${colors.primary[400]}`,
            '& .pro-sidebar-inner': {
              background: `${colors.primary[400]} !important`,
            },
            '& .pro-sidebar': {
              height: '100vh !important',
            },
            '& .pro-icon-wrapper': {
              backgroundColor: 'transparent !important',
            },
            '& .pro-inner-item': {
              padding: '5px 20px 5px 20px !important',
            },
            '& .pro-inner-item:hover': {
              color: '#868dfb !important',
            },
            '& .pro-menu-item.active': {
              color: '#6870fa !important',
            },
          }}
        >
          <ProSidebar collapsed={isCollapsed} width={240}>
            <Menu iconShape='square'>
              {/* LOGO AND MENU ICON */}
              <MenuItem
                onClick={() => setIsCollapsed(!isCollapsed)}
                icon={isCollapsed ? <MenuOutlined /> : undefined}
                style={{
                  margin: '10px 0 10px 0',
                  color: colors.grey[100],
                }}
              >
                {!isCollapsed && (
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    ml='15px'
                  >
                    <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                      <MenuOutlined />
                    </IconButton>
                  </Box>
                )}
              </MenuItem>
              {!isCollapsed && (
                <Box mb='15px'>
                  <Box
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <img
                      alt='User profile'
                      width='100px'
                      height='100px'
                      src={imageToDisplay}
                      style={{ cursor: 'pointer', borderRadius: '50%'}}
                      onClick={handleClickOpen}
                    />
                  </Box>
                  <Box textAlign='center'>
                    <Typography
                      variant='h3'
                      color={colors.grey[100]}
                      fontWeight='bold'
                      // sx={{ m: '10px 0 0 0' }}
                      sx={{ m: '10px 18px 0 18px', overflowWrap: 'break-word' }}

                    >
                      {firstName} {lastName}
                    </Typography>
                    <Typography 
                      variant='h6'
                      color={colors.greenAccent[500]}
                      lineHeight={1.3}
                      sx={{ m: '6px 18px 0 15px', overflowWrap: 'break-word' }}
                    >
                      {role}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* MENU ITEMS */}
              <Box paddingLeft={isCollapsed ? undefined : '4%'}>
                <Item
                  title='Dashboard'
                  to='/dashboard'
                  icon={<HomeOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                {!isCollapsed && (
                  <Typography
                    variant='h6'
                    color={colors.grey[300]}
                    sx={{ m: '10px 0 5px 20px' }}
                  >
                    Scope 1
                  </Typography>
                )}
                <Item
                  title='Petroleum Products'
                  to='/bars/oilgas'
                  icon={<OilBarrelOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                <Item
                  title='Coal'
                  to='/bars/coal'
                  icon={<FactoryOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                <Item
                  title='Wood'
                  to='/bars/wood'
                  icon={<ForestOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                <Item
                  title='Other fuel types'
                  to='/bars/otherfuel'
                  icon={<HelpCenterOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                <Item
                  title='Transport'
                  to='/bars/transport'
                  icon={<LocalShippingOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                {!isCollapsed && (
                  <Typography
                    variant='h6'
                    color={colors.grey[300]}
                    sx={{ m: '10px 0 5px 20px' }}
                  >
                    Scope 2
                  </Typography>
                )}
                <Item
                  title='Electricity'
                  to='/bars/electricity'
                  icon={<ElectricBoltOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                <Item
                  title='Steam & Heat'
                  to='/bars/steamheat'
                  icon={<LocalFireDepartmentIcon /> }
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                {!isCollapsed && (
                  <Typography
                    variant='h6'
                    color={colors.grey[300]}
                    sx={{ m: '10px 0 5px 20px' }}
                  >
                    Inventory
                  </Typography>
                )}
                <Item
                  title='GHG Inventory'
                  to='/bars/emissionsinventory'
                  icon={<ListAltOutlinedIcon />}
                  selected={selected}
                  setSelected={setSelected}
                  colors={colors}
                />
                {!isCollapsed && (
                  <Typography
                    variant='h6'
                    color={colors.grey[300]}
                    sx={{ m: '10px 0 5px 20px' }}
                  >
                    User
                  </Typography>
                )}
                <LogOutButton
                  title='Logout'
                  icon={<LogoutIcon />}
                  colors={colors}
                  onClick={handleSignOut}
                />
              </Box>
            </Menu>
          </ProSidebar>
        </Box>
      </nav>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{ width: '500px', margin: 'auto' }}
      >
        <DialogTitle sx={{ fontSize: 20 }}>EDIT</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Click through to edit some or all of your personal profile
            information.
          </DialogContentText>
          <form>
            <OneLineInput
              autoFocus
              margin='dense'
              id='firstName'
              label='First Name'
              type='text'
              fullWidth
              variant='standard'
              value={firstName || ''}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <OneLineInput
              autoFocus
              margin='dense'
              id='lastName'
              label='Last Name'
              type='text'
              fullWidth
              variant='standard'
              value={lastName || ''}
              onChange={(e) => setLastName(e.target.value)}
            />
            <OneLineInput
              autoFocus
              margin='dense'
              id='role'
              label='Role'
              type='text'
              fullWidth
              variant='standard'
              value={role || ''}
              onChange={(e) => setRole(e.target.value)}
            />
            <OneLineInput
              autoFocus
              margin='dense'
              id='profileImageUrl'
              label='Image URL'
              type='text'
              fullWidth
              variant='standard'
              value={imageUrl || ''}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={updateProfile}>Update</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SidebarPanel;

// This function location looks at the URL path and returns the
// name of the current page. This is used to determine which part
// item in the sidebar to make "selected/active.""
const checkInitialState = (location) => {
  switch (location.pathname) {
    case '/dashboard':
      return 'Dashboard';
    case '/bars/scope1':
      return 'Scope 1';
    case '/bars/scope 2':
      return 'Scope 2';
    case '/bars/ghginventory':
      return 'GHG Inventory';
    default:
      return 'Dashboard';
  }
};
